import { useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Button, SheetGrip } from '@/components/ui';
import { Icon } from '@/components/Icon';
import { api, type ContentReportTarget, type ReportCategory } from '@/lib/api';
import './reportSheet.css';
import { useBackHandler } from '@/hooks/useBackHandler';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { useT } from '@/i18n/react';


/** Mit jelentünk — a lap mindkettőre ugyanaz, csak a végpont más. */
export type ReportTarget =
  | { kind: 'user'; username: string }
  /** GRUNDO #62 (terv 1.4): az aktivitás-bejelentés addig `501`-et adott. */
  | { kind: 'activity'; activityId: string }
  /** #68: privát üzenet — csak a tartalmi okok értelmezhetők rá. */
  | { kind: 'message'; conversationId: string; messageId: string }
  /** #80 (App Store 1.2): hozzászólás, banda-poszt/-komment/-chat, banda — csak tartalmi okok. */
  | { kind: 'content'; content: ContentReportTarget };

const MESSAGE_CATEGORIES = new Set<ReportCategory>(['offensive', 'privacy', 'other']);

/**
 * Felhasználó vagy aktivitás bejelentése.
 *
 * A KATEGÓRIA KÖTELEZŐ, a leírás nem. Ennek moderációs oka van: a `docs/05`
 * szerint a kategória dönti el, melyik ágra kerül a bejelentés (`technical`
 * vagy `content`), és ezt az emberi olvasás nem tudja kiváltani. A szabad
 * szöveg viszont sokszor üres marad, és kár lenne emiatt elveszíteni a
 * bejelentést.
 *
 * A szövegek NEM ígérnek visszajelzést. A bejelentő a `firestore.rules`
 * szerint a saját bejelentését sem olvashatja vissza — ha az app azt írná,
 * hogy „értesítünk az eredményről", az hazugság lenne.
 *
 * Sikeres küldés után a lap NEM zárul be magától, hanem visszaigazolásra vált
 * (Geri, GRUNDO #62). Korábban azonnal eltűnt, és a képernyő értesítése a
 * látható részen kívül jelent meg — a bejelentő azt hihette, semmi sem
 * történt. Innen bármilyen bezárás `onDone`, nem `onClose`: a bejelentés már
 * elment.
 */

const NOTE_MAX = 500;

export function ReportSheet({
  target,
  title,
  onClose,
  onDone,
}: {
  target: ReportTarget;
  /** A lap címe, pl. „kata jelentése" vagy „Aktivitás jelentése". */
  title: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const [category, setCategory] = useState<ReportCategory | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const close = sent ? onDone : onClose;

  /* Android vissza gomb: ez a réteg záródjon, ne a képernyő váltson. */
  useBackHandler(true, () => close());
  // Lefelé húzva is lecsukható (#71); a visszaigazolásra váltáskor újraköt.
  const panelRef = useSwipeToClose<HTMLDivElement>(close, true, sent);

  function submit() {
    if (category === null) return;
    setBusy(true);
    setError('');
    (target.kind === 'user'
      ? api.reportUser(target.username, category, note)
      : target.kind === 'message'
        ? api.messages.report(target.conversationId, target.messageId, category, note)
        : target.kind === 'content'
          ? api.reportContent(target.content, category, note)
          : api.reportActivity(target.activityId, category, note)
    )
      .then(() => setSent(true))
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : t.reportSheet.submitFailed);
      })
      .finally(() => setBusy(false));
  }

  // Portálban (DECISIONS #52): transzformált ősben a `position: fixed` nem a
  // képernyőhöz igazodna.
  return createPortal(
    <div className="rsheet" role="dialog" aria-label={title}>
      <button type="button" className="rsheet__scrim" aria-label={t.common.close} onClick={close} />
      {sent ? (
        <ReportSent onDone={onDone} panelRef={panelRef} />
      ) : (
        <div ref={panelRef} className="rsheet__panel">
          <SheetGrip />
          <div className="rsheet__head">
            <span className="rsheet__title">{title}</span>
            <p className="rsheet__lead">
              {t.reportSheet.lead}
            </p>
          </div>

          <div className="rsheet__list">
            {reportCategories(t).filter((item) => (target.kind !== 'message' && target.kind !== 'content') || MESSAGE_CATEGORIES.has(item.value)).map((item) => (
              <button
                key={item.value}
                type="button"
                className={`rsheet__option${category === item.value ? ' rsheet__option--on' : ''}`}
                aria-pressed={category === item.value}
                onClick={() => setCategory(item.value)}
              >
                <span className="rsheet__option-label">{item.label}</span>
                <span className="rsheet__option-help">{item.help}</span>
              </button>
            ))}
          </div>

          <label className="rsheet__note">
            <span className="label">{t.reportSheet.noteLabel}</span>
            <textarea
              className="rsheet__textarea"
              rows={3}
              maxLength={NOTE_MAX}
              value={note}
              placeholder={t.reportSheet.notePlaceholder}
              onChange={(event) => setNote(event.target.value)}
            />
            <span className="rsheet__count">
              {note.length} / {NOTE_MAX}
            </span>
          </label>

          {error ? (
            <p className="field__error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="rsheet__actions">
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              {t.reportSheet.cancel}
            </Button>
            <Button variant="danger" loading={busy} disabled={category === null} onClick={submit}>
              {t.reportSheet.submit}
            </Button>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

/** Visszaigazolás a sikeres küldés után — ugyanazon a lapon, nem külön réteg. */
function ReportSent({ onDone, panelRef }: { onDone: () => void; panelRef: RefObject<HTMLDivElement> }) {
  const t = useT();
  return (
    <div ref={panelRef} className="rsheet__panel rsheet__panel--done" role="status">
      <SheetGrip />
      <span className="rsheet__done-icon">
        <Icon name="check" size={32} />
      </span>
      <span className="rsheet__title">{t.reportSheet.sentTitle}</span>
      <p className="rsheet__lead">
        {t.reportSheet.sentLead}
      </p>
      <Button block onClick={onDone}>
        {t.reportSheet.done}
      </Button>
    </div>
  );
}

function reportCategories(t: ReturnType<typeof useT>): { value: ReportCategory; label: string; help: string }[] {
  const { categories } = t.reportSheet;
  return [
    { value: 'gps_spoof', ...categories.gpsSpoof },
    { value: 'vehicle', ...categories.vehicle },
    { value: 'wrong_type', ...categories.wrongType },
    { value: 'offensive', ...categories.offensive },
    { value: 'privacy', ...categories.privacy },
    { value: 'other', ...categories.other },
  ];
}
